import React, { useRef, useState, useEffect } from 'react';
import { X, Download, Pencil, Copy, Check, Loader, Image as ImageIcon, Upload } from 'lucide-react';
import { motion } from 'framer-motion';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { normalizeSpotifyUri, uploadReleaseCover } from '../lib/releaseAssets';

interface ReleaseLike {
  id: string;
  name?: string | null;
  release_date?: string | null;
  cover_url?: string | null;
  spotify_uri?: string | null;
}

interface ReleaseAssetsModalProps {
  release: ReleaseLike;
  onClose: () => void;
  /** Called after a successful save so the parent can update its local copy. */
  onSaved?: (patch: Partial<ReleaseLike>) => void;
  /** Artist-context client for protected artists; defaults to the plain client. */
  db?: SupabaseClient | null;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export const ReleaseAssetsModal: React.FC<ReleaseAssetsModalProps> = ({
  release,
  onClose,
  onSaved,
  db,
}) => {
  const client = db || supabase;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [coverUrl, setCoverUrl] = useState(release.cover_url || '');
  const [uri, setUri] = useState(release.spotify_uri || '');
  const [uploadingCover, setUploadingCover] = useState(false);
  const [savingUri, setSavingUri] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The release prop usually comes from a cached/stale `releases` embed on
  // ad_creatives — refetch the row on open so edits always start from the
  // values actually stored in the DB.
  useEffect(() => {
    let cancelled = false;
    client
      .from('releases')
      .select('id, name, release_date, cover_url, spotify_uri')
      .eq('id', release.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled && data) {
          setCoverUrl(data.cover_url || '');
          setUri(data.spotify_uri || '');
        }
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [release.id]);

  const handleCoverPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    setUploadingCover(true);
    setError(null);
    try {
      const url = await uploadReleaseCover(client, release.id, file);
      setCoverUrl(url);
      onSaved?.({ cover_url: url });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cover upload failed');
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSaveUri = async () => {
    const normalized = normalizeSpotifyUri(uri);
    if (!normalized) {
      setError('Invalid Spotify URI — expected e.g. spotify:album:4itKk52E9ZCdWUQcFAkud9');
      return;
    }
    setSavingUri(true);
    setError(null);
    try {
      const { error: updateError } = await client
        .from('releases')
        .update({ spotify_uri: normalized })
        .eq('id', release.id);
      if (updateError) throw updateError;
      setUri(normalized);
      onSaved?.({ spotify_uri: normalized });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Saving failed');
    } finally {
      setSavingUri(false);
    }
  };

  const copyUri = async () => {
    if (!uri) return;
    await navigator.clipboard.writeText(uri).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const downloadUrl = coverUrl
    ? `${SUPABASE_URL}/functions/v1/force-download?url=${encodeURIComponent(coverUrl)}&filename=${encodeURIComponent(`${release.name || 'release'}-cover.jpg`)}`
    : '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
      <motion.div
        className="bg-white dark:bg-[#1F2937] rounded-lg max-w-md w-full p-6"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
      >
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold dark:text-white">
            {release.name || 'Release'}
            {release.release_date && (
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                {new Date(release.release_date).toLocaleDateString('de-DE', { day: 'numeric', month: 'numeric', year: 'numeric' })}
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="space-y-5">
          {/* Cover */}
          <div>
            <label className="block text-sm font-medium mb-2 dark:text-white">Cover</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              className="hidden"
              onChange={handleCoverPick}
            />
            {coverUrl ? (
              <div className="group relative w-32 h-32 rounded-md overflow-hidden border border-gray-200 dark:border-gray-600">
                <img src={coverUrl} alt="Cover" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingCover}
                    className="p-2 rounded-md bg-white/20 hover:bg-white/30 text-white"
                    title="Replace cover"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <a
                    href={downloadUrl}
                    className="p-2 rounded-md bg-white/20 hover:bg-white/30 text-white"
                    title="Download cover"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingCover}
                className="w-32 h-32 rounded-md border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-primary-500 hover:text-primary-500 transition-colors disabled:opacity-50"
              >
                {uploadingCover ? <Loader className="h-5 w-5 animate-spin" /> : <ImageIcon className="h-5 w-5" />}
                <span className="text-xs">{uploadingCover ? 'Uploading…' : 'Upload'}</span>
              </button>
            )}
          </div>

          {/* Spotify URI */}
          <div>
            <label className="block text-sm font-medium mb-2 dark:text-white">Spotify URI</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={uri}
                onChange={(e) => setUri(e.target.value)}
                placeholder="spotify:album:4itKk52E9ZCdWUQcFAkud9"
                className="flex-1 px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white font-mono focus:border-primary-500 focus:ring-primary-500"
              />
              <button
                type="button"
                onClick={copyUri}
                disabled={!uri}
                className="p-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-40"
                title="Copy URI"
              >
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={handleSaveUri}
                disabled={savingUri || !uri.trim() || uri === (release.spotify_uri || '')}
                className="px-3 py-2 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-40 flex items-center gap-1"
              >
                {savingUri ? <Loader className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Save
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              A Spotify link (open.spotify.com/…) is automatically converted to the URI.
              Guide: <a href="https://swipe.fm/spotify-uri" target="_blank" rel="noreferrer" className="text-primary-500 hover:underline">swipe.fm/spotify-uri</a>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
